import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import HolographicBg from "@/components/HolographicBg";
import TopBar from "@/components/TopBar";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsBlocked, setEventsBlocked] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?redirect=/dashboard"); return; }
    // Accounts are for members + influencers + admin only. A user without
    // membership has nothing to see here — send them to the membership gate.
    if (user.role === "user" && !user.membership_active) {
      navigate("/membership");
      return;
    }
    api.get("/dashboard/me").then(({ data }) => setData(data)).catch(() => {});
    api.get("/events")
      .then(({ data }) => { setEvents(data); setEventsBlocked(false); })
      .catch((err) => {
        if (err.response?.status === 403) setEventsBlocked(true);
      });
  }, [user, loading, navigate]);

  if (!data) return <div className="relative min-h-screen"><HolographicBg /><TopBar /><p className="relative z-20 text-center mt-20 font-mono text-white/60">Loading...</p></div>;

  const canSeeEvents = data.membership_active || data.user.role === "admin" || data.user.role === "influencer";

  return (
    <div className="relative min-h-screen">
      <HolographicBg />
      <TopBar />
      <main className="relative z-20 px-6 py-10 max-w-6xl mx-auto" data-testid="member-dashboard">
        <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80">— inner circle —</div>
        <h1 className="font-display text-4xl font-black uppercase mt-2">Welcome, {data.user.full_name?.split(" ")[0] || "Disciple"}</h1>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          <Stat label="Membership" value={data.membership_active ? "ACTIVE" : "INACTIVE"} accent={data.membership_active ? "text-cyan-400" : "text-white/40"} />
          <Stat label="Discount" value={data.membership_active ? "15% OFF" : "—"} accent="text-fuchsia-400" />
          <Stat label="Tickets" value={data.tickets.length} accent="text-white" />
        </div>

        {canSeeEvents && (
          <section className="mt-12">
            <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Upcoming events</h2>
            <p className="font-mono text-[11px] uppercase tracking-widest text-cyan-400/70 mt-2">
              ◆ Disciple early view · public gates open later
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {events.length === 0 && <div className="font-mono text-sm text-white/50">No events scheduled yet.</div>}
              {events.map((e) => (
                <Link key={e.id} to={`/event/${e.id}`} className="glass rounded-sm p-5 hover:border-cyan-400/40 transition" data-testid={`event-card-${e.id}`}>
                  <div className="font-display text-lg font-bold uppercase">{e.name}</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/50 mt-1">{e.date}</div>
                  <div className="font-mono text-xs uppercase tracking-widest text-white/40 mt-3">{e.remaining} / {e.capacity} remaining</div>
                  <div className="font-mono text-xs uppercase tracking-widest mt-3">
                    <span className="text-cyan-400">${e.price} reserve</span>
                    {e.door_price > 0 && <span className="text-fuchsia-400"> · ${e.door_price} door</span>}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {eventsBlocked && !canSeeEvents && (
          <section className="mt-12">
            <div className="font-mono text-xs text-white/50 uppercase tracking-widest">Events are members-only.</div>
          </section>
        )}

        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold uppercase">Your tickets</h2>
          <div className="space-y-3 mt-4">
            {data.tickets.length === 0 && <div className="font-mono text-sm text-white/50">No tickets yet.</div>}
            {data.tickets.map((t) => (
              <div key={t.id} className="glass rounded-sm p-4 flex items-center justify-between" data-testid={`ticket-${t.id}`}>
                <div>
                  <div className="font-display font-bold uppercase">{t.event?.name || "Event"}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t.event?.date}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-xs uppercase tracking-widest text-cyan-400">{t.status}</div>
                  {t.qr_token && <div className="font-mono text-[10px] text-white/40 mt-1">QR: {t.qr_token.slice(0, 8)}…</div>}
                </div>
              </div>
            ))}
          </div>
        </section>
        <ChangePasswordCard accent="cyan" showTempKeyBanner={!!data.user.temp_key_issued_at} />
      </main>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className="glass rounded-sm p-5">
    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">{label}</div>
    <div className={`font-display text-2xl font-black uppercase mt-2 ${accent}`}>{value}</div>
  </div>
);
